import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addAbout, editAbout } from '../../../../api/endpoints/cmsSite';
import type { About } from '../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';

/** The 4 `type` values actually observed on live rows (see api/types/cmsSite.ts's `About`
 *  doc comment) — offered as free-editable suggestions, not an enforced enum, since swagger
 *  types this field as a bare string. */
const ABOUT_TYPE_OPTIONS = ['About', 'Vision', 'Mission', 'Activities'] as const;

const schema = z.object({
  title: z.string().min(1, 'Başlık gerekli'),
  summary: z.string().optional(),
  content: z.string().optional(),
  type: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { title: '', summary: '', content: '', type: '' };

function toFormValues(about: About): FormValues {
  return {
    title: about.title ?? '',
    summary: about.summary ?? '',
    content: about.content ?? '',
    type: about.type ?? '',
  };
}

/** Create/edit dialog for Hakkımızda (Site About). Confirmed via local-backend swagger +
 *  live curl AND live row data (see api/types/cmsSite.ts header). Always rendered inside a
 *  specific site's tab, so `site_Id` is injected from the `siteId` prop. */
export function AboutFormDialog({
  open,
  about,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  about: About | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!about;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(about ? toFormValues(about) : emptyValues);
  }, [open, about, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && about
        ? editAbout({ id: about.id, site_Id: siteId, ...values })
        : addAbout({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Hakkımızda kaydı güncellendi.' : 'Hakkımızda kaydı oluşturuldu.', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['cms', 'abouts'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Hakkımızda Kaydını Güncelle' : 'Yeni Hakkımızda Kaydı'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={[...ABOUT_TYPE_OPTIONS]}
                    value={field.value ?? ''}
                    onChange={(_e, value) => field.onChange(value ?? '')}
                    onInputChange={(_e, value) => field.onChange(value)}
                    renderInput={(params) => <TextField {...params} size="small" label="Tip" />}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Özet" {...register('summary')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={4} label="İçerik" {...register('content')} />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
